import { forwardRef, useEffect, useRef } from 'react';
import { Group, Rect, Line, Path, Text } from 'react-konva';
import Konva from 'konva';
import { Node } from '../../../../core/models/scene';
import { AI_FLOWCHART_SHAPES } from '../../../process/shapes/flowchart/FlowchartShapes.defs';
import { useDiagramStore } from '../../../../stores/diagramStore';
import { useThemeAwareColor } from '../../../../react-app/theme/ThemeProvider';

type TextAlign = 'left' | 'center' | 'right' | 'justify';
type VerticalAlign = 'top' | 'middle' | 'bottom';

type FlowchartVariant = string;

const getAiDef = (variant: string) =>
  (AI_FLOWCHART_SHAPES as Record<string, { width: number; height: number; paths: string[]; fillPaths?: string[] }>)[variant];

interface FlowchartShapeNodeProps {
  node: Node;
  selected: boolean;
  onDragStart?: (pos: { x: number; y: number }) => void;
  onDragMove?: (pos: { x: number; y: number }) => void;
  onDragEnd?: (pos: { x: number; y: number }) => void;
  onClick?: (e: any) => void;
  onDoubleClick?: (e: any) => void;
  onTransformStart?: (e: any) => void;
  onTransformEnd?: (e: any) => void;
  onTransform?: (e: any) => void;
  onStartTextEdit?: (textNode: any) => void;
  editing?: boolean;
  canWrite?: boolean;
  liveSize?: { w: number; h: number };
}

const resolveVariant = (node: Node): FlowchartVariant => {
  const attrs = (node.attrs as Record<string, unknown>) ?? {};
  const candidates = [attrs.variant, attrs.shapeType, (node as any).name, node.type];
  for (const candidate of candidates) {
    if (typeof candidate === 'string') {
      const normalized = candidate.toLowerCase().replace(/\s+/g, '-') as FlowchartVariant;
      // Do not restrict to known set; allow custom variants for legacy shapes
      return normalized;
    }
  }
  return 'process';
};

const FlowchartShapeNodeBase = forwardRef<Konva.Group, FlowchartShapeNodeProps>(({
  node,
  selected,
  onDragStart,
  onDragMove,
  onDragEnd,
  onClick,
  onDoubleClick: _onDoubleClick,
  onTransformStart,
  onTransformEnd,
  onTransform,
  onStartTextEdit,
  editing = false,
  canWrite = true,
  liveSize,
}, ref) => {
  const baseWidth = (node.width ?? node.w ?? 140) as number;
  const baseHeight = (node.height ?? node.h ?? 80) as number;
  const width = liveSize?.w ?? baseWidth;
  const height = liveSize?.h ?? baseHeight;
  const variant = resolveVariant(node);
  const attrs = (node.attrs as Record<string, unknown>) ?? {};

  const groupRef = useRef<Konva.Group | null>(null);
  const aiShapeGroupRef = useRef<Konva.Group | null>(null);
  const shapeRef = useRef<Konva.Shape | Konva.Line | Konva.Path | null>(null);
  const textRef = useRef<Konva.Text | null>(null);
  const boundsRef = useRef<Konva.Rect | null>(null);
  const lastAutoHeightRef = useRef<number>(0);
  const baselineInnerHeightRef = useRef<{ key: string; value: number }>({ key: '', value: 0 });

  const getThemeColor = useThemeAwareColor();

  const fillColor =
    (node.fillColor as string) ||
    (attrs.fillColor as string) ||
    '#F8FAFC';
  const strokeColor =
    (node.strokeColor as string) ||
    (attrs.strokeColor as string) ||
    '#1E293B';
  const themeFillColor = getThemeColor(fillColor, { type: 'background' });
  const themeStrokeColor = getThemeColor(strokeColor, { type: 'foreground' });
  const strokeWidth =
    typeof attrs.strokeWidth === 'number'
      ? (attrs.strokeWidth as number)
      : typeof node.strokeWidth === 'number'
        ? node.strokeWidth
        : 2;
  const stageScale = useDiagramStore((s) => (s.stage?.scale ?? 1));

  // Read text from multiple possible locations
  const textValue =
    typeof node.text === 'string' && node.text.trim().length > 0
      ? node.text
      : (typeof attrs.label === 'string' && attrs.label.trim().length > 0)
        ? attrs.label
        : '';
  const textColor = (attrs.textColor as string) || '#0F172A';
  const themeTextColor = getThemeColor(textColor, { type: 'foreground' });
  const fontSize = (attrs.fontSize as number) || (node.fontSize as number) || 12;
  const fontFamily = (attrs.fontFamily as string) || (node.fontFamily as string) || 'Inter, sans-serif';
  const hAlign = (attrs.textAlign as TextAlign) || 'center';
  const vAlign = (attrs.verticalAlign as VerticalAlign) || 'middle';
  const fontStyle = (() => {
    const fw = (attrs.fontWeight || '').toString().toLowerCase();
    const fs = (attrs.fontStyle || '').toString().toLowerCase();
    const isBold = fw === 'bold' || fw === '700';
    const isItalic = fs === 'italic';
    if (isBold && isItalic) return 'bold italic';
    if (isBold) return 'bold';
    if (isItalic) return 'italic';
    return 'normal';
  })();
  const textDecoration = (attrs.textDecoration as string) || 'none';
  
  // Ensure text is always visible when there's content
  const hasText = textValue && textValue.trim().length > 0;
  
  // Ensure textRef is always available for double-click editing
  useEffect(() => {
    if (!textRef.current && groupRef.current) {
      // Text element should be rendered, but if ref isn't set, find it
      const textNodes = (groupRef.current as any).find?.('Text') || [];
      if (textNodes.length > 0 && !textRef.current) {
        textRef.current = textNodes[0];
      }
    }
  }, [textValue, editing]);

  const dash = Array.isArray(attrs.dash) ? attrs.dash : [];
  const dashEnabled = !!attrs.dashEnabled && dash.length > 0;
  const snapToGrid = 12;

  const fireDrag = (handler?: (pos: { x: number; y: number }) => void) => (e: any) => {
    if (!handler) return;
    const pos = e.target.position();
    handler({ x: pos.x, y: pos.y });
  };

  const fireDragSnapped = (handler?: (pos: { x: number; y: number }) => void) => (e: any) => {
    const pos = e.target.position();
    const gx = Math.round(pos.x / snapToGrid) * snapToGrid;
    const gy = Math.round(pos.y / snapToGrid) * snapToGrid;
    try { e.target.position({ x: gx, y: gy }); } catch {}
    if (handler) handler({ x: gx, y: gy });
  };

  const applyDimensions = (nextWidth: number, nextHeight: number) => {
    const group = groupRef.current;
    const shape = shapeRef.current as any;
    if (group) {
      group.width(nextWidth);
      group.height(nextHeight);
    }
    if (shape) {
      const aiDef = getAiDef(variant);
      if (aiDef) {
        const sx = aiDef.width > 0 ? nextWidth / aiDef.width : 1;
        const sy = aiDef.height > 0 ? nextHeight / aiDef.height : 1;
        const aiGroup = aiShapeGroupRef.current as any;
        if (aiGroup) {
          try {
            aiGroup.scaleX(sx);
            aiGroup.scaleY(sy);
            aiGroup.x(0);
            aiGroup.y(0);
          } catch {}
        } else {
          try { shape.scaleX(sx); shape.scaleY(sy); } catch {}
        }
      }
      if (!aiDef) {
        switch (variant) {
        case 'process': {
          const rect = shape as Konva.Rect;
          rect.width(nextWidth);
          rect.height(nextHeight);
          rect.cornerRadius(6);
          break;
        }
        case 'decision': {
          const line = shape as Konva.Line;
          line.points([
            nextWidth / 2, 0,
            nextWidth, nextHeight / 2,
            nextWidth / 2, nextHeight,
            0, nextHeight / 2,
          ]);
          break;
        }
        case 'terminator': {
          const rect = shape as Konva.Rect;
          const radius = Math.min(nextWidth, nextHeight) / 2;
          rect.width(nextWidth);
          rect.height(nextHeight);
          rect.cornerRadius(radius);
          break;
        }
        case 'data': {
          const line = shape as Konva.Line;
          const cut = Math.min(nextWidth * 0.3, nextHeight * 0.4);
          line.points([
            cut, 0,
            nextWidth, 0,
            nextWidth - cut, nextHeight,
            0, nextHeight,
          ]);
          break;
        }
        case 'document': {
          const path = shape as Konva.Path;
          const wave = Math.min(nextHeight * 0.3, 24);
          const pathData = [
            `M0 0`,
            `L${nextWidth} 0`,
            `L${nextWidth} ${nextHeight - wave}`,
            `Q${nextWidth * 0.75} ${nextHeight} ${nextWidth * 0.5} ${nextHeight - wave / 2}`,
            `Q${nextWidth * 0.25} ${nextHeight - wave} 0 ${nextHeight}`,
            `Z`,
          ].join(' ');
          path.data(pathData);
          break;
        }
        case 'database':
        case 'display':
        case 'stored-data':
        case 'direct-data':
        case 'sequential-access':
        case 'off-page-ref':
        case 'delay': {
          const path = shape as Konva.Path;
          const newW = nextWidth;
          const newH = nextHeight;
          const data = (() => {
            if (variant === 'database') {
              const radius = Math.min(newW * 0.15, newH * 0.15, 12);
              return [
                `M${radius} 0`,
                `A${newW / 2 - radius} ${radius} 0 0 1 ${newW - radius} 0`,
                `L${newW - radius} ${newH - radius}`,
                `A${newW / 2 - radius} ${radius} 0 0 1 ${radius} ${newH - radius}`,
                `L${radius} 0`,
                `Z`,
                `M${radius} ${newH - radius}`,
                `A${newW / 2 - radius} ${radius} 0 0 0 ${newW - radius} ${newH - radius}`,
              ].join(' ');
            }
            if (variant === 'display') {
              const curve = Math.min(newW * 0.2, 30);
              return [
                `M${curve} 0`,
                `Q0 ${newH / 2} ${curve} ${newH}`,
                `L${newW - curve} ${newH}`,
                `L${newW} ${newH / 2}`,
                `L${newW - curve} 0`,
                `Z`,
              ].join(' ');
            }
            if (variant === 'stored-data') {
              const curve = Math.min(newW * 0.15, 20);
              return [
                `M${curve} 0`,
                `L${newW - curve} 0`,
                `Q${newW} ${newH / 2} ${newW - curve} ${newH}`,
                `L${curve} ${newH}`,
                `Q0 ${newH / 2} ${curve} 0`,
                `Z`,
              ].join(' ');
            }
            if (variant === 'direct-data') {
              const curve = Math.min(newW * 0.15, 20);
              return [
                `M0 ${curve}`,
                `Q${newW / 2} 0 ${newW} ${curve}`,
                `L${newW} ${newH - curve}`,
                `Q${newW / 2} ${newH} 0 ${newH - curve}`,
                `Z`,
              ].join(' ');
            }
            if (variant === 'sequential-access') {
              const radius = Math.min(newW, newH) / 2 - 2;
              const centerX = newW / 2;
              const centerY = newH / 2;
              const tailLength = Math.min(newW * 0.25, 15);
              return [
                `M${centerX} ${centerY - radius}`,
                `A${radius} ${radius} 0 1 1 ${centerX} ${centerY + radius}`,
                `A${radius} ${radius} 0 1 1 ${centerX} ${centerY - radius}`,
                `M${centerX + radius} ${centerY}`,
                `Q${centerX + radius + tailLength} ${centerY} ${centerX + radius + tailLength} ${centerY + tailLength}`,
              ].join(' ');
            }
            if (variant === 'off-page-ref') {
              const chevron = Math.min(newW * 0.3, 30);
              return [
                `M0 0`,
                `L${newW} 0`,
                `L${newW} ${newH - chevron}`,
                `L${newW / 2} ${newH}`,
                `L0 ${newH - chevron}`,
                `Z`,
              ].join(' ');
            }
            const curve = Math.min(newW * 0.2, 25);
            return [
              `M0 0`,
              `L${newW - curve} 0`,
              `Q${newW} ${newH / 2} ${newW - curve} ${newH}`,
              `L0 ${newH}`,
              `Z`,
            ].join(' ');
          })();
          if (typeof data === 'string' && data.length > 0) {
            path.data(data);
          }
          break;
        }
        case 'manual-input':
        case 'manual-operation':
        case 'preparation': {
          const line = shape as Konva.Line;
          // These shapes use Line, update points based on variant
          if (variant === 'manual-input') {
            const offset = Math.min(nextWidth * 0.2, 20);
            line.points([offset, 0, nextWidth, 0, nextWidth - offset, nextHeight, 0, nextHeight]);
          } else if (variant === 'manual-operation') {
            const offset = Math.min(nextWidth * 0.2, 20);
            line.points([offset, 0, nextWidth - offset, 0, nextWidth, nextHeight, 0, nextHeight]);
          } else if (variant === 'preparation') {
            const offset = Math.min(nextWidth * 0.15, 20);
            line.points([
              offset, 0,
              nextWidth - offset, 0,
              nextWidth, nextHeight / 2,
              nextWidth - offset, nextHeight,
              offset, nextHeight,
              0, nextHeight / 2,
            ]);
          }
          break;
        }
        case 'predefined-process':
        case 'internal-storage': {
          // These use Group with Rect and Lines, update Rect dimensions
          const group = shape as Konva.Group;
          if (group) {
            const rect = group.findOne('Rect') as Konva.Rect;
            if (rect) {
              rect.width(nextWidth);
              rect.height(nextHeight);
            }
            // Update divider lines
            if (variant === 'predefined-process') {
              const lines = group.find('Line');
              if (lines.length >= 2) {
                (lines[0] as Konva.Line).points([nextWidth / 3, 0, nextWidth / 3, nextHeight]);
                (lines[1] as Konva.Line).points([(nextWidth * 2) / 3, 0, (nextWidth * 2) / 3, nextHeight]);
              }
            } else if (variant === 'internal-storage') {
              const lines = group.find('Line');
              if (lines.length >= 2) {
                (lines[0] as Konva.Line).points([0, nextHeight / 3, nextWidth, nextHeight / 3]);
                (lines[1] as Konva.Line).points([0, (nextHeight * 2) / 3, nextWidth, (nextHeight * 2) / 3]);
              }
            }
          }
          break;
        }
        case 'loop-limit': {
          const rect = shape as Konva.Rect;
          const radius = Math.min(nextWidth * 0.1, nextHeight * 0.2, 15);
          rect.width(nextWidth);
          rect.height(nextHeight);
          rect.cornerRadius(radius);
          break;
        }
        default:
          break;
        }
      }
    }
    const innerX = 12;
    const innerY = 12;
    const innerW = Math.max(0, nextWidth - 24);
    const innerH = Math.max(0, nextHeight - 24);
    if (boundsRef.current) {
      boundsRef.current.x(innerX);
      boundsRef.current.y(innerY);
      boundsRef.current.width(innerW);
      boundsRef.current.height(innerH);
    }
    if (textRef.current) {
      textRef.current.x(innerX);
      textRef.current.y(innerY);
      textRef.current.width(innerW);
      textRef.current.height(innerH);
    }
  };

  useEffect(() => {
    applyDimensions(width, height);
  }, [width, height, variant]);

  useEffect(() => {
    const bounds = boundsRef.current as any;
    if (!bounds) return;

    const measureBaselineInnerHeight = (innerW: number): number => {
      const key = `${innerW}:${fontSize}:${fontFamily}:${fontStyle}:${textDecoration}:${hAlign}:${vAlign}`;
      if (baselineInnerHeightRef.current.key === key && baselineInnerHeightRef.current.value > 0) {
        return baselineInnerHeightRef.current.value;
      }
      try {
        const t = new Konva.Text({
          text: 'A',
          fontSize,
          fontFamily,
          fontStyle,
          textDecoration,
          width: innerW,
          align: hAlign,
          verticalAlign: vAlign,
        } as any);
        const rect = t.getClientRect({ skipShadow: true });
        const h = rect?.height ?? 0;
        // EditableTextOverlay enforces a minimum editor height of 18px (in canvas units),
        // so treat that as the true single-line baseline to avoid runaway growth on single letters.
        const baselineRaw = typeof h === 'number' && Number.isFinite(h) && h > 0 ? h : Math.max(1, fontSize * 1.2);
        const baseline = Math.max(18, baselineRaw);
        baselineInnerHeightRef.current = { key, value: baseline };
        return baseline;
      } catch {
        const baseline = Math.max(18, fontSize * 1.2);
        baselineInnerHeightRef.current = { key, value: baseline };
        return baseline;
      }
    };

    const onBoundsHeightChange = () => {
      try {
        const innerH = typeof bounds.height === 'function' ? (bounds.height() as number) : 0;
        if (!Number.isFinite(innerH) || innerH <= 0) return;

        const group = groupRef.current as any;
        if (!group) return;
        const curH = (group?.height?.() as number) || height;
        const curW = (group?.width?.() as number) || width;
        const innerW = curW - 24;
        if (!Number.isFinite(innerW) || innerW <= 0) return;

        const baseline = measureBaselineInnerHeight(innerW);

        // Ignore single-line jitter; only grow when text bounds clearly exceed single-line height.
        if (innerH <= baseline + 2) return;

        const desiredH = Math.max(20, curH, innerH + 24);
        if (desiredH <= curH + 0.5) return;
        if (desiredH <= (lastAutoHeightRef.current || 0) + 0.5) return;

        lastAutoHeightRef.current = desiredH;
        applyDimensions(curW, desiredH);
        try { group?.getLayer?.()?.batchDraw?.(); } catch {}
        if (selected) {
          try {
            const stage = group?.getStage?.();
            const trs = stage?.find?.('Transformer') || [];
            for (const tr of trs) {
              try { tr.forceUpdate?.(); } catch {}
              try { tr.getLayer?.()?.batchDraw?.(); } catch {}
            }
          } catch {}
        }
      } catch {}
    };

    bounds.on('heightChange', onBoundsHeightChange);
    return () => {
      try { bounds.off('heightChange', onBoundsHeightChange); } catch {}
    };
  }, [width, height, variant, selected, fontSize, fontFamily, fontStyle, textDecoration, hAlign, vAlign]);

  const handleTransformEnd = (e: any) => {
    // Let the canvas-level transform handler capture scale/position and commit it;
    // don’t reset dimensions here to avoid dropping the resized size.
    onTransformEnd?.(e);
  };

  const handleTransform = (e: any) => {
    const target = e.target as any;
    const scaleX = target.scaleX?.() ?? 1;
    const scaleY = target.scaleY?.() ?? 1;
    const curW = (groupRef.current?.width?.() as number) || width;
    const curH = (groupRef.current?.height?.() as number) || height;
    const nextW = Math.max(20, curW * scaleX);
    const nextH = Math.max(20, curH * scaleY);
    applyDimensions(nextW, nextH);
    try { target.scaleX(1); target.scaleY(1); } catch {}
    onTransform?.(e);
  };

  const renderVariant = () => {
    const shared = {
      fill: themeFillColor,
      stroke: themeStrokeColor,
      strokeWidth,
      strokeEnabled: strokeWidth > 0,
      perfectDrawEnabled: false,
      dash: dashEnabled ? dash : undefined,
      dashEnabled: dashEnabled,
    };

    const aiDef = getAiDef(variant);

    if (aiDef) {
      const zoomScaledStrokeWidth = strokeWidth * stageScale;
      const fillPaths = (aiDef.fillPaths && aiDef.fillPaths.length > 0) ? aiDef.fillPaths : null;
      const aiScaleX = aiDef.width > 0 ? width / aiDef.width : 1;
      const aiScaleY = aiDef.height > 0 ? height / aiDef.height : 1;
      const aiOffsetX = 0;
      const aiOffsetY = 0;
      if (aiDef.paths.length <= 1) {
        return (
          <>
            <Group
              ref={aiShapeGroupRef as any}
              x={aiOffsetX}
              y={aiOffsetY}
              scaleX={aiScaleX}
              scaleY={aiScaleY}
            >
              {fillPaths?.map((d, idx) => (
                <Path
                  key={`ai-flowchart-fill-${idx}`}
                  {...shared}
                  data={d}
                  strokeEnabled={false}
                  fillRule="evenodd"
                />
              ))}
              <Path
                ref={shapeRef as any}
                {...shared}
                data={aiDef.paths[0] ?? ''}
                strokeScaleEnabled={false}
                fillRule="evenodd"
                fill={fillPaths ? 'transparent' : (shared as any).fill}
                strokeWidth={zoomScaledStrokeWidth}
              />
            </Group>
          </>
        );
      }
      return (
        <>
          <Group
            ref={aiShapeGroupRef as any}
            x={aiOffsetX}
            y={aiOffsetY}
            scaleX={aiScaleX}
            scaleY={aiScaleY}
          >
            {fillPaths?.map((d, idx) => (
              <Path
                key={`ai-flowchart-fill-${idx}`}
                {...shared}
                data={d}
                strokeEnabled={false}
                fillRule="evenodd"
              />
            ))}
            {aiDef.paths.map((d, idx) => (
              <Path
                key={`ai-flowchart-path-${idx}`}
                ref={idx === 0 ? (shapeRef as any) : undefined}
                {...shared}
                data={d}
                strokeScaleEnabled={false}
                fillRule="evenodd"
                fill={fillPaths ? 'transparent' : (shared as any).fill}
                strokeWidth={zoomScaledStrokeWidth}
              />
            ))}
          </Group>
        </>
      );
    }

    switch (variant) {
      case 'process':
        return (
          <Rect
            ref={shapeRef as any}
            {...shared}
            x={0}
            y={0}
            width={width}
            height={height}
            cornerRadius={6}
          />
        );
      case 'decision':
        return (
          <Line
            ref={shapeRef as any}
            {...shared}
            x={0}
            y={0}
            points={[
              width / 2, 0,
              width, height / 2,
              width / 2, height,
              0, height / 2,
            ]}
            closed
          />
        );
      case 'terminator': {
        const radius = Math.min(width, height) / 2;
        return (
          <Rect
            ref={shapeRef as any}
            {...shared}
            x={0}
            y={0}
            width={width}
            height={height}
            cornerRadius={radius}
          />
        );
      }
      case 'data': {
        const cut = Math.min(width * 0.3, height * 0.4);
        return (
          <Line
            ref={shapeRef as any}
            {...shared}
            points={[
              cut, 0,
              width, 0,
              width - cut, height,
              0, height,
            ]}
            closed
          />
        );
      }
      case 'document': {
        const wave = Math.min(height * 0.3, 24);
        const pathData = [
          `M0 0`,
          `L${width} 0`,
          `L${width} ${height - wave}`,
          `Q${width * 0.75} ${height} ${width * 0.5} ${height - wave / 2}`,
          `Q${width * 0.25} ${height - wave} 0 ${height}`,
          `Z`,
        ].join(' ');
        return (
          <Path
            ref={shapeRef as any}
            {...shared}
            data={pathData}
          />
        );
      }
      case 'database': {
        // Cylinder shape: oval top, vertical sides, oval bottom
        const radius = Math.min(width * 0.15, height * 0.15, 12);
        const pathData = [
          `M${radius} 0`,
          `A${width / 2 - radius} ${radius} 0 0 1 ${width - radius} 0`,
          `L${width - radius} ${height - radius}`,
          `A${width / 2 - radius} ${radius} 0 0 1 ${radius} ${height - radius}`,
          `L${radius} 0`,
          `Z`,
          `M${radius} ${height - radius}`,
          `A${width / 2 - radius} ${radius} 0 0 0 ${width - radius} ${height - radius}`,
        ].join(' ');
        return (
          <Path
            ref={shapeRef as any}
            {...shared}
            data={pathData}
          />
        );
      }
      case 'manual-input': {
        // Parallelogram tilted to the right
        const offset = Math.min(width * 0.2, 20);
        return (
          <Line
            ref={shapeRef as any}
            {...shared}
            points={[
              offset, 0,
              width, 0,
              width - offset, height,
              0, height,
            ]}
            closed
          />
        );
      }
      case 'predefined-process': {
        // Rectangle with vertical dividers
        return (
          <Group ref={shapeRef as any}>
            <Rect
              {...shared}
              x={0}
              y={0}
              width={width}
              height={height}
            />
            <Line
              {...shared}
              points={[width / 3, 0, width / 3, height]}
            />
            <Line
              {...shared}
              points={[(width * 2) / 3, 0, (width * 2) / 3, height]}
            />
          </Group>
        );
      }
      case 'display': {
        // Monitor/speaker shape: curved left, pointed right
        const curve = Math.min(width * 0.2, 30);
        const pathData = [
          `M${curve} 0`,
          `Q0 ${height / 2} ${curve} ${height}`,
          `L${width - curve} ${height}`,
          `L${width} ${height / 2}`,
          `L${width - curve} 0`,
          `Z`,
        ].join(' ');
        return (
          <Path
            ref={shapeRef as any}
            {...shared}
            data={pathData}
          />
        );
      }
      case 'stored-data': {
        // Drum shape: curved sides inwards
        const curve = Math.min(width * 0.15, 20);
        const pathData = [
          `M${curve} 0`,
          `L${width - curve} 0`,
          `Q${width} ${height / 2} ${width - curve} ${height}`,
          `L${curve} ${height}`,
          `Q0 ${height / 2} ${curve} 0`,
          `Z`,
        ].join(' ');
        return (
          <Path
            ref={shapeRef as any}
            {...shared}
            data={pathData}
          />
        );
      }
      case 'direct-data': {
        // Barrel shape: curved sides outwards
        const curve = Math.min(width * 0.15, 20);
        const pathData = [
          `M0 ${curve}`,
          `Q${width / 2} 0 ${width} ${curve}`,
          `L${width} ${height - curve}`,
          `Q${width / 2} ${height} 0 ${height - curve}`,
          `Z`,
        ].join(' ');
        return (
          <Path
            ref={shapeRef as any}
            {...shared}
            data={pathData}
          />
        );
      }
      case 'internal-storage': {
        // Rectangle with horizontal dividers
        return (
          <Group ref={shapeRef as any}>
            <Rect
              {...shared}
              x={0}
              y={0}
              width={width}
              height={height}
            />
            <Line
              {...shared}
              points={[0, height / 3, width, height / 3]}
            />
            <Line
              {...shared}
              points={[0, (height * 2) / 3, width, (height * 2) / 3]}
            />
          </Group>
        );
      }
      case 'sequential-access': {
        // Circle with tail (Q shape)
        const radius = Math.min(width, height) / 2 - 2;
        const centerX = width / 2;
        const centerY = height / 2;
        const tailLength = Math.min(width * 0.25, 15);
        const pathData = [
          `M${centerX} ${centerY - radius}`,
          `A${radius} ${radius} 0 1 1 ${centerX} ${centerY + radius}`,
          `A${radius} ${radius} 0 1 1 ${centerX} ${centerY - radius}`,
          `M${centerX + radius} ${centerY}`,
          `Q${centerX + radius + tailLength} ${centerY} ${centerX + radius + tailLength} ${centerY + tailLength}`,
        ].join(' ');
        return (
          <Path
            ref={shapeRef as any}
            {...shared}
            data={pathData}
          />
        );
      }
      case 'off-page-ref': {
        // Rectangle with chevron bottom
        const chevron = Math.min(width * 0.3, 30);
        const pathData = [
          `M0 0`,
          `L${width} 0`,
          `L${width} ${height - chevron}`,
          `L${width / 2} ${height}`,
          `L0 ${height - chevron}`,
          `Z`,
        ].join(' ');
        return (
          <Path
            ref={shapeRef as any}
            {...shared}
            data={pathData}
          />
        );
      }
      case 'delay': {
        // Rectangle with curved right side
        const curve = Math.min(width * 0.2, 25);
        const pathData = [
          `M0 0`,
          `L${width - curve} 0`,
          `Q${width} ${height / 2} ${width - curve} ${height}`,
          `L0 ${height}`,
          `Z`,
        ].join(' ');
        return (
          <Path
            ref={shapeRef as any}
            {...shared}
            data={pathData}
          />
        );
      }
      case 'manual-operation': {
        // Trapezoid (wider at top)
        const offset = Math.min(width * 0.2, 20);
        return (
          <Line
            ref={shapeRef as any}
            {...shared}
            points={[
              offset, 0,
              width - offset, 0,
              width, height,
              0, height,
            ]}
            closed
          />
        );
      }
      case 'loop-limit': {
        // Rounded rectangle on top and bottom
        const radius = Math.min(width * 0.1, height * 0.2, 15);
        return (
          <Rect
            ref={shapeRef as any}
            {...shared}
            x={0}
            y={0}
            width={width}
            height={height}
            cornerRadius={radius}
          />
        );
      }
      case 'preparation': {
        // Hexagon
        const offset = Math.min(width * 0.15, 20);
        return (
          <Line
            ref={shapeRef as any}
            {...shared}
            points={[
              offset, 0,
              width - offset, 0,
              width, height / 2,
              width - offset, height,
              offset, height,
              0, height / 2,
            ]}
            closed
          />
        );
      }
      default:
        return null;
    }
  };

  const handleGroupDoubleClick = (e: any) => {
    e.cancelBubble = true;
    if (onStartTextEdit) {
      // Try textRef first
      if (textRef.current) {
        onStartTextEdit(textRef.current);
        return;
      }
      // Fallback: try to find the text node in the group
      const group = groupRef.current;
      if (group) {
        const textNodes = (group as any).find?.('Text') || [];
        if (textNodes.length > 0) {
          onStartTextEdit(textNodes[0]);
          return;
        }
      }
      // Last resort: create a synthetic text node reference
      // This shouldn't happen, but if it does, we'll try to trigger editing via the event bus
      console.warn('FlowchartShapeNode: Could not find text node for editing');
    }
  };

  const textBoundsX = 12;
  const textBoundsY = 12;
  const textBoundsW = Math.max(0, width - 24);
  const textBoundsH = Math.max(0, height - 24);

  return (
    <Group
      ref={(nodeEl) => {
        groupRef.current = nodeEl as Konva.Group | null;
        if (typeof ref === 'function') ref(nodeEl as any);
        else if (ref && typeof ref === 'object') (ref as any).current = nodeEl;
      }}
      id={node.id}
      name="shape"
      x={node.x}
      y={node.y}
      width={width}
      height={height}
      draggable={canWrite}
      onDragStart={fireDrag(onDragStart)}
      onDragMove={fireDrag(onDragMove)}
      onDragEnd={fireDragSnapped(onDragEnd)}
      onClick={onClick}
      onDblClick={canWrite ? handleGroupDoubleClick : undefined}
      onDblTap={canWrite ? handleGroupDoubleClick : undefined}
      onTransformStart={onTransformStart}
      onTransformEnd={handleTransformEnd}
      onTransform={handleTransform}
    >
      <Group
        name="rotation-group"
        x={width / 2}
        y={height / 2}
        offsetX={width / 2}
        offsetY={height / 2}
        onTransformStart={onTransformStart}
        onTransformEnd={handleTransformEnd}
      >
        <Group name="shape-content" width={width} height={height} onTransform={onTransform}>
          {renderVariant()}
        </Group>
      </Group>
      <Rect
        ref={boundsRef as any}
        name="text-bounds"
        x={textBoundsX}
        y={textBoundsY}
        width={textBoundsW}
        height={textBoundsH}
        listening={false}
        fill="rgba(0,0,0,0)"
        strokeEnabled={false}
        perfectDrawEnabled={false}
        disableOverlayLiveResize={true}
      />
      <Text
        ref={textRef as any}
        x={textBoundsX}
        y={textBoundsY}
        width={textBoundsW}
        height={textBoundsH}
        text={textValue || ''}
        align={hAlign}
        verticalAlign={vAlign}
        listening={canWrite && !editing}
        visible={!editing}
        fontSize={fontSize}
        fontFamily={fontFamily}
        fontStyle={fontStyle}
        textDecoration={textDecoration}
        fill={hasText ? themeTextColor : 'transparent'}
        onDblClick={(e: any) => {
          e.cancelBubble = true;
          if (onStartTextEdit && textRef.current) {
            onStartTextEdit(textRef.current);
          }
        }}
        onDblTap={(e: any) => {
          e.cancelBubble = true;
          if (onStartTextEdit && textRef.current) {
            onStartTextEdit(textRef.current);
          }
        }}
      />
    </Group>
  );
});
FlowchartShapeNodeBase.displayName = 'FlowchartShapeNode';

export const FlowchartShapeNode = FlowchartShapeNodeBase;

export default FlowchartShapeNode;
